/* eslint-disable no-unused-vars */
import { useTrackingStore } from '@/stores/tracking/trackingStore';

import useApiHelpers from '@/composables/api/helpers';
import usePaymentEndpoints from '@/composables/api/usePaymentEndpoints';
import useModalActions from '@/composables/store/modal/useModalActions';
import useProgressBarActions from '@/composables/progress/useProgressBarActions';
import { expressCheckoutEnabled } from '@/composables/router/handleExpressCheckout';

const { updateToComplete, updateToPending, resetAllProgress } = useProgressBarActions();
const { paymentCallback } = usePaymentEndpoints();
const { toggleOrderRecoveryModal } = useModalActions();
const { getRequest } = useApiHelpers();
const routes = [
  {
    path: '/',
    name: 'order',
    component: () => import('@/views/OrderView.vue'),
    beforeEnter(to, from, next) {
      resetAllProgress();
      // Vue Router: pass previous route to view for handleApiRedirect safety checks
      // eslint-disable-next-line no-param-reassign
      to.meta = { ...to.meta, fromRoute: from ? { name: from.name } : undefined };
      next();
    },
  },
  {
    path: '/recovery/:orderId?',
    name: 'order-recovery',
    component: () => import('@/views/OrderRecoveryView.vue'),
    beforeEnter(to, from, next) {
      if (to.params.orderId) {
        const trackingStore = useTrackingStore();
        trackingStore.recordOrderKey(to.params.orderId);
      }
      toggleOrderRecoveryModal();
      resetAllProgress();
      next();
    },
  },
  {
    path: '/recovery/opt-out/:orderId?',
    name: 'order-recovery.opt-out',
    component: () => import('@/views/OrderRecoveryOptOutView.vue'),
  },
  {
    path: '/papi/transit/',
    name: 'order.papi',
    component: () => import('@/views/OrderView.vue'),
    beforeEnter(to, from, next) {
      resetAllProgress();
      // Vue Router: pass previous route to view for handleApiRedirect safety checks
      // eslint-disable-next-line no-param-reassign
      to.meta = { ...to.meta, fromRoute: from ? { name: from.name } : undefined };
      next();
    },
  },
  {
    path: '/verification',
    name: 'verification',
    component: () => import('@/views/VerificationView.vue'),
    beforeEnter() {
      updateToPending('verification');
    },
  },
  {
    path: '/verification/code',
    name: 'verificationCode',
    component: () => import('@/views/VerificationCodeView.vue'),
  },
  {
    path: '/payment',
    name: 'payment',
    component: () => import('@/views/payment/PaymentView.vue'),
  },
  {
    path: '/payment/plaid',
    name: 'payment.plaid.relink',
    component: () => import('@/views/plaidRedirect/PlaidRedirectView.vue'),
    async beforeEnter(to, from, next) {
      updateToComplete(['verification', 'identity']);
      updateToPending('payment');
      next();
    },
    children: [
      {
        path: 'relink',
        component: () => import('@/views/plaidRedirect/OpenBankingView.vue'),
      },
      {
        path: 'relinkach',
        component: () => import('@/views/plaidRedirect/AchView.vue'),
      },
    ],
  },
  {
    path: '/payment/:payment/callback/:status',
    name: 'payment.callback',
    component: () => import('@/views/PaymentView.vue'),
    beforeEnter(to, from, next) {
      if (to.query.orderKey) {
        const trackingStore = useTrackingStore();
        trackingStore.recordOrderKey(to.query.orderKey);
      }
      paymentCallback(to, next);
    },
  },
  {
    path: '/payment/:payment',
    name: 'payment.payment',
    component: () => import('@/views/PaymentView.vue'),
    async beforeEnter(to, from, next) {
      if (!expressCheckoutEnabled()) {
        updateToComplete(['verification', 'identity']);
      }
      updateToPending('payment');
      next();
    },
  },
  {
    path: '/coindeposit/*',
    name: 'coindeposit',
    component: () => import('@/views/CoinDepositView.vue'),
    beforeEnter() {
      updateToComplete(['verification', 'identity']);
      updateToPending('payment');
    },
  },
  {
    path: '/deposit',
    name: 'deposit',
    component: () => import('@/views/DepositView.vue'),
  },
  {
    path: '/coindeposit/:orderId',
    name: 'coindeposit',
    component: () => import('@/views/CoinDepositView.vue'),
    beforeEnter() {
      updateToComplete(['verification', 'identity']);
      updateToPending('payment');
    },
  },
  {
    path: '/status/:orderId',
    name: 'summary',
    component: () => import('@/views/OrderSummaryView.vue'),
    beforeEnter(to, from, next) {
      const trackingStore = useTrackingStore();
      trackingStore.recordOrderKey(to.params.orderId);
      updateToComplete(['verification', 'identity', 'payment']);
      next();
    },
  },
  {
    path: '/widget',
    name: 'widget',
    component: () => import('@/views/WidgetView.vue'),
    beforeEnter() {
      resetAllProgress();
    },
  },
  {
    path: '/error/:errorCode?',
    name: 'error',
    component: () => import('@/views/ErrorView.vue'),
    props: (route) => ({
      errorCode: route.params.errorCode || route.query.errorCode || '',
    }),
  },
  {
    path: '/:pathMatch(.*)*', // Updated catch-all route definition
    name: 'not-found',
    redirect: { name: 'error', query: { errorCode: '404' } }, // Redirect to the 'error' route
  },
  {
    path: '/cancel/:orderId',
    name: 'cancel.order',
    component: () => import('@/views/PaymentCoolingView.vue'),
    async beforeEnter(to, from, next) {
      const trackingStore = useTrackingStore();
      trackingStore.recordOrderKey(to.params.orderId);
      await getRequest(`/cancel/${to.params.orderId}`, () => {
        next(`/status/${to.params.orderId}`);
      });
    },
  },
  {
    path: '/auth/google/callback',
    name: 'auth.google.callback',
    component: () => import('@/views/AuthCallbackView.vue'),
  },
  {
    path: '/recurring/opt-out/:orderId?',
    name: 'recurring.opt-out',
    component: () => import('@/views/RecurringOrderOptOutView.vue'),
  },
];

export default routes;
